import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, CheckCircle2, Mail, Calendar } from 'lucide-react';
import { Button } from '../ui/button';
import { motion, AnimatePresence } from 'framer-motion';

interface Event {
    id: number;
    title: string;
    date: string;
    duration: string;
    format: string;
    description: string;
    topics: string[];
    status: 'upcoming' | 'finished';
    daysLeft?: number;
    registerUrl?: string;
    videoUrl?: string;
}

const events: Event[] = [
    {
        id: 1,
        title: 'Simulacro Entrevista',
        date: 'lunes, 12 de enero de 2026, 07:00 p.m.',
        duration: '1 hora',
        format: 'Online (Google Meet)',
        description: 'Verás una entrevista técnica simulada en tiempo real y aprenderás cómo manejarla',
        topics: [
            'Entrevista técnica simulada en vivo',
            'Análisis de respuestas correctas e incorrectas',
            'Tips para responder preguntas difíciles',
            'Q&A con preguntas del público'
        ],
        status: 'finished',
        videoUrl: 'https://www.youtube.com/watch?v=nilQEsZEq6E&t=1463s'
    },
    {
        id: 2,
        title: 'Workshop: Posicionando perfil LinkedIn',
        date: 'lunes, 1 de diciembre de 2025, 07:00 p.m.',
        duration: '1.5 horas',
        format: 'Online (Google Meet)',
        description: 'Aprende a posicionar tu perfil de LinkedIn, armar un CV que atraiga reclutadores y te haga llegar oportunidades de trabajo',
        topics: [
            'Cómo optimizar tu perfil de LinkedIn para atraer reclutadores',
            'Keywords que buscan los reclutadores en LinkedIn y CV',
            'Qué poner y qué NO poner en tu CV',
            'Review de perfiles y CVs en vivo',
            'Tips para destacar sin experiencia previa'
        ],
        status: 'finished',
        videoUrl: 'https://www.youtube.com/watch?v=cYQLa5GURM0&t'
    },
    {
        id: 3,
        title: 'Charla Introductoria a la ingeniería de datos',
        date: 'jueves, 25 de septiembre de 2025, 06:00 p.m.',
        duration: '1 hora',
        format: 'Online (YouTube)',
        description: 'Aprende qué es la ingeniería de datos, qué skills necesitas y cómo empezar',
        topics: [
            'Qué es la ingeniería de datos',
            'Qué skills necesitas',
            'Cómo empezar'
        ],
        status: 'finished',
        videoUrl: ''
    },
    {
        id: 4,
        title: 'Webinar: Databricks + SQL',
        date: 'lunes, 3 de noviembre de 2025, 07:00 p.m.',
        duration: '1.5 horas',
        format: 'Online (Google Meet)',
        description: 'Descubre cómo Databricks revoluciona el procesamiento de datos con SQL a escala',
        topics: [
            'Introducción a Databricks y su ecosistema',
            'SQL en Databricks: queries optimizadas',
            'Casos de uso reales en empresas',
            'Demo en vivo de procesamiento de datos',
            'Q&A con expertos'
        ],
        status: 'finished',
        videoUrl: ''
    }
];

export default function Events() {
    const [page, setPage] = useState(0);
    const eventsPerPage = 2;
    const totalPages = Math.ceil(events.length / eventsPerPage);
    const visibleEvents = events.slice(page * eventsPerPage, (page + 1) * eventsPerPage);

    // Auto-rotate carousel every 10 seconds
    useEffect(() => {
        if (totalPages > 1) {
            const interval = setInterval(() => {
                setPage((prev) => (prev + 1) % totalPages);
            }, 10000);
            return () => clearInterval(interval);
        }
    }, [totalPages]);

    const goNext = () => setPage((prev) => (prev + 1) % totalPages);
    const goPrev = () => setPage((prev) => (prev - 1 + totalPages) % totalPages);

    return (
        <section id="eventos" className="py-24 bg-white overflow-hidden">
            <div className="max-w-7xl mx-auto px-6">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true }}
                >
                    <div className="text-center mb-12">
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">🎓 Eventos Gratuitos</h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">Workshops y webinars gratis para que empieces tu camino en Data</p>
                        <div className="w-20 h-1 bg-blue-600 mx-auto mt-6"></div>
                    </div>

                    {/* Dots navigation */}
                    <div className="flex justify-center gap-2 mb-6">
                        {Array.from({ length: totalPages }).map((_, index) => (
                            <button
                                key={index}
                                onClick={() => setPage(index)}
                                className={`h-2 rounded-full transition-all duration-500 ease-out ${index === page ? 'w-8 bg-purple-600' : 'w-2 bg-gray-300 hover:bg-gray-400'}`}
                                aria-label={`Ir a página ${index + 1}`}
                            />
                        ))}
                    </div>

                    {/* Events grid with smooth transitions */}
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={page}
                            initial={{ opacity: 0, x: 50 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -50 }}
                            transition={{ duration: 0.4, ease: "easeInOut" }}
                            className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto"
                        >
                            {visibleEvents.map((event, idx) => (
                                <motion.div
                                    key={event.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: idx * 0.1 }}
                                >
                                    <div className="bg-gradient-to-br from-purple-50 to-blue-50 border-2 border-purple-200 rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 h-full">
                                        <div className="text-center mb-4">
                                            <span className="text-4xl">📅</span>
                                            <div className="mt-4">
                                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${event.status === 'finished' ? 'bg-gray-500 text-white' : 'bg-green-500 text-white'
                                                    }`}>
                                                    {event.status === 'finished' ? 'Finalizado' : `Faltan ${event.daysLeft} días`}
                                                </span>
                                            </div>
                                            <h3 className="text-2xl font-bold text-gray-900 mt-4 mb-4">{event.title}</h3>
                                            <div className="space-y-2 text-sm text-gray-700">
                                                <p><strong>📅 Fecha:</strong> {event.date}</p>
                                                <p><strong>⏱️ Duración:</strong> {event.duration}</p>
                                                <p><strong>💻 Formato:</strong> {event.format}</p>
                                            </div>
                                        </div>

                                        <p className="text-sm text-gray-600 mb-4 text-center">{event.description}</p>

                                        <div className="space-y-2 mb-6">
                                            {event.topics.map((topic, topicIdx) => (
                                                <div key={topicIdx} className="flex items-center gap-2">
                                                    <CheckCircle2 className="w-4 h-4 text-purple-500 flex-shrink-0" />
                                                    <span className="text-sm text-gray-700">{topic}</span>
                                                </div>
                                            ))}
                                        </div>

                                        {event.status === 'finished' ? (
                                            <div className="space-y-3">
                                                <div className="bg-gray-100 p-4 rounded-lg border border-gray-300">
                                                    <p className="text-sm text-gray-700 font-medium text-center">📹 Evento finalizado - Mira la grabación</p>
                                                </div>
                                                <a
                                                    href={event.videoUrl || '#'}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className={`w-full py-3 rounded-lg flex items-center justify-center gap-2 font-medium transition-all duration-300 ${event.videoUrl ? 'bg-gray-600 hover:bg-gray-700 text-white' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                        }`}
                                                >
                                                    Ver grabación
                                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                                                </a>
                                            </div>
                                        ) : (
                                            <div className="space-y-3">
                                                <div className="bg-purple-100 p-4 rounded-lg border border-purple-300">
                                                    <p className="text-sm text-purple-900 font-medium text-center">📧 Inscripciones abiertas</p>
                                                </div>
                                                <Button 
                                                    className="w-full bg-purple-600 hover:bg-purple-700 text-white transition-all duration-300"
                                                    onClick={() => event.registerUrl && window.open(event.registerUrl, '_blank')}
                                                >
                                                    Unirme al evento <Mail className="ml-2 w-5 h-5" />
                                                </Button>
                                                <Button 
                                                    variant="outline" 
                                                    className="w-full border-purple-300 text-purple-700 hover:bg-purple-50 transition-all duration-300"
                                                    onClick={() => event.registerUrl && window.open(event.registerUrl, '_blank')}
                                                >
                                                    📅 Agregar a mi calendario <Calendar className="ml-2 w-4 h-4" />
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                </motion.div>
                            ))}
                        </motion.div>
                    </AnimatePresence>

                    {/* Navigation arrows */}
                    {totalPages > 1 && (
                        <div className="flex justify-center gap-4 mt-8">
                            <button
                                onClick={goPrev}
                                className="px-4 py-2 border border-gray-300 rounded-full hover:bg-gray-50 transition-all duration-300 hover:scale-105"
                            >
                                <ChevronLeft className="w-5 h-5" />
                            </button>
                            <button
                                onClick={goNext}
                                className="px-4 py-2 border border-gray-300 rounded-full hover:bg-gray-50 transition-all duration-300 hover:scale-105"
                            >
                                <ChevronRight className="w-5 h-5" />
                            </button>
                        </div>
                    )}
                </motion.div>
            </div>
        </section>
    );
}
